import { useMemo, useState, type CSSProperties } from 'react'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import type { HuntableGameSpecies } from '../../lib/huntable-game-catalog'
import type { PlanHuntGameClass, PlanHuntGameSelection } from './types'

export type { PlanHuntGameSelection }

export interface PlanHuntGameStepProps {
  catalog: readonly HuntableGameSpecies[]
  selections: PlanHuntGameSelection[]
  onChange: (next: PlanHuntGameSelection[]) => void
}

/** Per-class quota stepper bounds. A class quota of `null` means "No limit". */
const MIN_LIMIT = 1
const MAX_LIMIT = 999

const stepBtnStyle: CSSProperties = {
  width: 26, height: 26, borderRadius: 7, flexShrink: 0,
  border: `1px solid ${COLORS.surfaceContainerHighest}`, background: COLORS.surfaceContainerLowest,
  color: COLORS.onSurface, fontSize: 15, fontWeight: 700, lineHeight: 1, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
}

/** The class dropdown's trigger — reads "Classes · <summary> ⌄". */
const triggerStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', maxWidth: 420,
  padding: '6px 10px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
  border: `1.5px solid ${COLORS.outlineVariant}`, background: COLORS.surfaceContainerLowest,
  color: COLORS.onSurface,
}

export function PlanHuntGameStep({ catalog, selections, onChange }: PlanHuntGameStepProps) {
  const { t } = useTranslation('planHunt')
  const [query, setQuery] = useState('')
  // Only one species' class dropdown is open at a time: it keeps the list short
  // and keeps class labels unambiguous between species that share a class name
  // (Moose and Red deer both have a "Calf").
  const [openSpecies, setOpenSpecies] = useState<string | null>(null)

  const selectedByName = useMemo(() => new Map(selections.map((s) => [s.species, s])), [selections])

  // Search matches the species name or any of its classes, so "bull" finds Moose.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return catalog
    return catalog.filter(
      (s) => s.name.toLowerCase().includes(q) || s.classes.some((c) => c.toLowerCase().includes(q)),
    )
  }, [catalog, query])

  const patch = (species: string, classes: PlanHuntGameClass[]) =>
    onChange(selections.map((s) => (s.species === species ? { ...s, classes } : s)))

  // Checking a species never opens its classes: the second row appears with the
  // dropdown closed ("All classes"), so picking a whole species stays one click
  // and the list keeps its height.
  const toggleSpecies = (name: string) => {
    if (selectedByName.has(name)) {
      if (openSpecies === name) setOpenSpecies(null)
      onChange(selections.filter((s) => s.species !== name))
    } else {
      onChange([...selections, { species: name, classes: [] }])
    }
  }
  // A class carries its own quota (`limit`), defaulting to null = "No limit" when
  // first picked. Toggling a class off drops its quota with it.
  const toggleClass = (species: string, cls: string, current: PlanHuntGameClass[]) => {
    const on = current.some((c) => c.name === cls)
    patch(species, on ? current.filter((c) => c.name !== cls) : [...current, { name: cls, limit: null }])
  }
  const setAllClasses = (species: string, allClasses: readonly string[], current: PlanHuntGameClass[]) => {
    const allOn = current.length === allClasses.length
    patch(
      species,
      allOn
        ? []
        : allClasses.map((name) => current.find((c) => c.name === name) ?? { name, limit: null }),
    )
  }
  const updateClassLimit = (species: string, cls: string, limit: number | null, current: PlanHuntGameClass[]) =>
    patch(species, current.map((c) => (c.name === cls ? { ...c, limit } : c)))

  const clearAll = () => {
    setOpenSpecies(null)
    onChange([])
  }

  const summary =
    selections.length === 0
      ? t('game.noneSelected')
      : selections
          .map((s) =>
            s.classes.length > 0 ? `${s.species} (${s.classes.map((c) => c.name).join(', ')})` : s.species,
          )
          .join(', ')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 360 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: COLORS.outline }}>
            <Icon name="search" size={16} />
          </span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('game.searchPlaceholder')}
            style={{
              width: '100%',
              padding: '9px 12px 9px 34px',
              fontSize: 13,
              border: `1.5px solid ${COLORS.outlineVariant}`,
              borderRadius: 999,
              outline: 'none',
              background: COLORS.surfaceContainerLowest,
              color: COLORS.onSurface,
            }}
          />
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: COLORS.outline, fontWeight: 600 }}>
          {t('game.selectedCount', { selected: selections.length, total: catalog.length })}
        </span>
        <button
          type="button"
          className="wh-btn wh-btn-outline"
          style={{ height: 32, padding: '0 10px', fontSize: 12 }}
          onClick={clearAll}
          disabled={selections.length === 0}
        >
          {t('game.clear')}
        </button>
      </div>

      <div className="wh-card" style={{ overflow: 'hidden' }}>
        {filtered.map((species, i) => {
          const sel = selectedByName.get(species.name)
          const active = sel !== undefined
          const chosen = sel?.classes ?? []
          const hasClasses = species.classes.length > 0
          const allChosen = hasClasses && chosen.length === species.classes.length
          const open = openSpecies === species.name
          // No specific class picked still means "all classes count", so the
          // trigger reads "All classes" rather than looking empty.
          const classSummary = chosen.length === 0 ? t('game.allClasses') : chosen.map((c) => c.name).join(', ')
          return (
            <div
              key={species.name}
              style={{
                borderBottom: i === filtered.length - 1 ? 'none' : `1px solid ${COLORS.surfaceContainerHighest}`,
                background: active ? COLORS.primaryOpacity8 : COLORS.surfaceContainerLowest,
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '9px 14px',
                  cursor: 'pointer',
                }}
              >
                <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700 }}>{species.name}</span>
                {hasClasses && !active && (
                  <span style={{ fontSize: 11, color: COLORS.outline }}>
                    {t('game.classCount', { count: species.classes.length })}
                  </span>
                )}
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleSpecies(species.name)}
                  aria-label={species.name}
                  className="wh-checkbox"
                />
              </label>

              {/* Second row — only for a checked species that has classes. */}
              {active && hasClasses && (
                <div style={{ padding: '0 14px 10px' }}>
                  <button
                    type="button"
                    onClick={() => setOpenSpecies(open ? null : species.name)}
                    aria-expanded={open}
                    aria-label={t('game.classesFor', { species: species.name })}
                    style={triggerStyle}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        color: COLORS.outline,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {t('game.classes')}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {classSummary}
                    </span>
                    <Icon
                      name="chevron-down"
                      size={16}
                      style={{ transform: open ? 'rotate(180deg)' : 'none', color: COLORS.outline }}
                    />
                  </button>

                  {open && (
                    <div
                      style={{
                        marginTop: 6,
                        maxWidth: 420,
                        borderRadius: 8,
                        border: `1px solid ${COLORS.outlineVariant}`,
                        background: COLORS.surfaceContainerLowest,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '6px 10px',
                          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
                        }}
                      >
                        <span style={{ flex: 1, fontSize: 11, color: COLORS.outline }}>
                          {chosen.length === 0 ? t('game.noClassPicked') : t('game.noLimitHint')}
                        </span>
                        <button
                          type="button"
                          onClick={() => setAllClasses(species.name, species.classes, chosen)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: COLORS.primary,
                            cursor: 'pointer',
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '2px 4px',
                            flexShrink: 0,
                          }}
                        >
                          {allChosen ? t('game.clearClasses') : t('game.selectAllClasses')}
                        </button>
                      </div>
                      {species.classes.map((cls) => {
                        const picked = chosen.find((c) => c.name === cls)
                        const on = picked !== undefined
                        const limit = picked?.limit ?? null
                        return (
                          <div
                            key={cls}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 10px',
                              borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
                            }}
                          >
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flex: 1,
                                minWidth: 0,
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="checkbox"
                                className="wh-checkbox"
                                checked={on}
                                onChange={() => toggleClass(species.name, cls, chosen)}
                                aria-label={cls}
                              />
                              <span style={{ fontSize: 13, fontWeight: on ? 600 : 500, color: on ? COLORS.onSurface : COLORS.outline }}>
                                {cls}
                              </span>
                            </label>
                            {/* Per-class quota: a stepper, or "No limit" when unset. */}
                            {on && (
                              <>
                                {limit == null ? (
                                  <span style={{ fontSize: 12, color: COLORS.outline }}>{t('game.noLimit')}</span>
                                ) : (
                                  <div
                                    role="group"
                                    aria-label={t('game.classBagLimit', { cls, species: species.name })}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                                  >
                                    <button
                                      type="button"
                                      aria-label={t('game.decreaseClassLimit', { cls })}
                                      onClick={() => updateClassLimit(species.name, cls, Math.max(MIN_LIMIT, limit - 1), chosen)}
                                      disabled={limit <= MIN_LIMIT}
                                      style={{ ...stepBtnStyle, opacity: limit <= MIN_LIMIT ? 0.4 : 1 }}
                                    >
                                      −
                                    </button>
                                    <span
                                      data-testid={`limit-${species.name}-${cls}`}
                                      style={{ minWidth: 20, textAlign: 'center', fontSize: 13, fontWeight: 700 }}
                                    >
                                      {limit}
                                    </span>
                                    <button
                                      type="button"
                                      aria-label={t('game.increaseClassLimit', { cls })}
                                      onClick={() => updateClassLimit(species.name, cls, Math.min(MAX_LIMIT, limit + 1), chosen)}
                                      disabled={limit >= MAX_LIMIT}
                                      style={{ ...stepBtnStyle, opacity: limit >= MAX_LIMIT ? 0.4 : 1 }}
                                    >
                                      +
                                    </button>
                                  </div>
                                )}
                                <label
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    fontSize: 11,
                                    color: COLORS.outline,
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    className="wh-checkbox"
                                    checked={limit == null}
                                    onChange={() => updateClassLimit(species.name, cls, limit == null ? MIN_LIMIT : null, chosen)}
                                    aria-label={t('game.noLimitForClass', { cls })}
                                  />
                                  {t('game.noLimit')}
                                </label>
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
        {filtered.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: COLORS.outline, fontSize: 13 }}>
            {t('game.noMatch', { query })}
          </div>
        )}
      </div>

      <div
        style={{
          padding: '12px 14px',
          borderRadius: 8,
          background: COLORS.surface,
          border: `1px solid ${COLORS.surfaceContainerHighest}`,
          fontSize: 13,
          color: COLORS.onSurface,
          lineHeight: 1.5,
        }}
      >
        <b>{t('game.selectedLabel')}</b> {summary}
      </div>
    </div>
  )
}
